import React from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

// Clean up junk seed values (e.g. single-letter product_type 'X') so the
// card doesn't render nonsense for test or partially-hydrated orders.
const cleanProduct = (s) => {
  const v = (s || '').trim();
  return v.length >= 3 ? v : '';
};
const cleanDim = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const formatSize = (item) => {
  // item.size may already contain the unit (e.g. "3.5x3.5 inches" — set by
  // useOrderSubmission). Return as-is in that case to avoid the
  // "3.5x3.5 inches inches" double-unit bug.
  if (item.size && typeof item.size === 'string' && item.size.trim().length > 1) {
    const s = item.size.trim();
    if (/\b(in|inch|inches|cm|mm)\b/i.test(s)) return s;
    return `${s} ${item.unit || 'in'}`;
  }
  const w = cleanDim(item.width);
  const h = cleanDim(item.height);
  if (w && h) return `${w}x${h} ${item.unit || 'in'}`;
  return 'Size not set';
};

/**
 * Left-column card on the Review Order page listing everything in the cart
 * with a prominent "Add New Item" CTA.
 *
 * Purely presentational — parent passes `items`, the formatter, the
 * add-item handler, and (iter114f) an `onRemoveItem(itemId)` callback
 * that wires through to DELETE /enhanced-orders/{order_id}/remove-item/{id}.
 */
export const OrderItemsCard = ({ items = [], formatCurrency, onAddItem, onRemoveItem }) => {
  const count = items.length;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6" data-testid="order-items-card">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">
        {count} Item{count !== 1 ? 's' : ''} in order
      </h2>

      {count > 0 ? (
        <div className="space-y-4">
          {items.map((item, index) => {
            const product = cleanProduct(item.product_type) || 'Custom sticker';
            return (
              <div key={item.id || `item-${index}`} className="border-b pb-4 last:border-b-0 relative">
                {/* iter114f: per-item remove. Customers asked for the
                    ability to drop an item from a multi-item order
                    when they only want to check out a subset. The
                    button is gated on `onRemoveItem` so admin / read-
                    only views don't render the trash icon. We never
                    let the user remove the LAST item — that path is
                    "delete the whole draft" and lives on the
                    Dashboard. */}
                {onRemoveItem && count > 1 && item.id && (
                  <button
                    type="button"
                    onClick={() => onRemoveItem(item.id)}
                    title="Remove this item from the order"
                    aria-label="Remove this item from the order"
                    data-testid={`order-items-remove-${item.id}`}
                    className="absolute top-0 right-0 p-1.5 rounded-md text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <p className="font-semibold text-gray-800">
                  <span className="text-gray-600">Product:</span> {product}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Size:</span> {formatSize(item)}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Quantity:</span> {item.quantity || 1}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Total Product Price:</span>{' '}
                  {formatCurrency(item.price || 0)}
                </p>
                {item.name && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Name:</span> {item.name}
                  </p>
                )}
                {item.instructions && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Instructions:</span> {item.instructions}
                  </p>
                )}
                {item.white_ink_option && (
                  <p
                    className="text-sm text-sky-700 font-medium mt-1"
                    data-testid={`item-white-ink-${item.id || index}`}
                  >
                    <span className="font-semibold">White Ink:</span>{' '}
                    {item.white_ink_option === 'clear'
                      ? 'Make white areas clear'
                      : 'Keep white in design'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-500">No items in order</p>
      )}

      <Button
        onClick={onAddItem}
        className="w-full mt-6 bg-[#4A7C59] hover:bg-[#3d6849] text-white"
        data-testid="order-items-add-btn"
      >
        + ADD NEW ITEM TO MY ORDER
      </Button>
    </div>
  );
};
